import { useMemo, useState } from 'react'
import { Check, Copy } from 'lucide-react'
import { Button } from '@/components/ui/button'

type SdkTab = 'python' | 'javascript' | 'curl'

interface AuditSdkSnippetsProps {
  baseUrl: string
  token: string
  entriesUrl: string
}

function pythonSnippet(baseUrl: string, token: string): string {
  return `from qolaig_audit import Audit

audit = Audit(
    base_url="${baseUrl}",
    token="${token}",
)
audit.capture_stdout()
audit.capture_logging()

# Events métier (optionnel)
audit.emit("order.created", {"id": 42})
audit.flush()`
}

function jsSnippet(baseUrl: string, token: string): string {
  return `import { Audit } from '@qolaig/audit'

const audit = new Audit({
  baseUrl: '${baseUrl}',
  token: '${token}',
})
audit.captureConsole()
audit.captureStdout() // Node only

await audit.emit('order.created', { id: 42 })
await audit.flush()`
}

function curlSnippet(entriesUrl: string, token: string): string {
  return `curl -X POST "${entriesUrl}" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${token}" \\
  -d '{"key":"login","value":{"user":"alice"},"metadata":{"source":"app"}}'`
}

const TABS: { id: SdkTab; label: string }[] = [
  { id: 'python', label: 'Python' },
  { id: 'javascript', label: 'JavaScript' },
  { id: 'curl', label: 'curl' },
]

export function AuditSdkSnippets({ baseUrl, token, entriesUrl }: AuditSdkSnippetsProps) {
  const [tab, setTab] = useState<SdkTab>('python')
  const [copied, setCopied] = useState(false)

  const code = useMemo(() => {
    if (tab === 'python') return pythonSnippet(baseUrl.replace(/\/$/, ''), token)
    if (tab === 'javascript') return jsSnippet(baseUrl.replace(/\/$/, ''), token)
    return curlSnippet(entriesUrl, token)
  }, [tab, baseUrl, token, entriesUrl])

  const installHint =
    tab === 'python'
      ? 'pip install qolaig-audit  # ou: pip install -e packages-qolaig/qolaig-audit-python'
      : tab === 'javascript'
        ? 'npm install @qolaig/audit  # ou chemin local packages-qolaig/qolaig-audit-js'
        : null

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      /* ignore */
    }
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <p className="text-xs font-medium text-gray-600">Brancher le SDK</p>
        <div className="flex items-center gap-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${
                tab === t.id
                  ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                  : 'text-gray-500 hover:text-gray-800'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      {installHint ? (
        <p className="text-[11px] text-gray-500 font-mono mb-2 break-all">{installHint}</p>
      ) : null}
      <div className="relative">
        <pre className="text-xs text-gray-800 bg-gray-100 border border-gray-200 p-3 rounded-md overflow-x-auto whitespace-pre-wrap font-mono">
          {code}
        </pre>
        <Button
          type="button"
          size="icon"
          variant="outline"
          className="absolute top-2 right-2 h-8 w-8 bg-white"
          onClick={copy}
          aria-label="Copier le snippet"
        >
          {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
        </Button>
      </div>
      <p className="mt-2 text-[11px] text-gray-500">
        Les logs capturés et les <code className="font-mono">emit()</code> apparaissent dans le terminal
        ci-dessous.
      </p>
    </div>
  )
}
